import type { StocktakeSummary, Warehouse } from '@erp/shared';
import { ClipboardCheck, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, Combobox, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';

const STATUS_TONE: Record<string, 'success' | 'secondary' | 'danger' | 'default'> = {
  posted: 'success',
  draft: 'secondary',
  cancelled: 'danger',
};

export function StocktakesPage(): JSX.Element {
  const { t, i18n } = useTranslation('stocktake');
  const navigate = useNavigate();
  const canWrite = usePermission('stock.adjust');
  const [rows, setRows] = useState<StocktakeSummary[] | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void api().get<{ items: StocktakeSummary[] }>('/stocktakes').then((r) => setRows(r.data.items));
    void api().get<{ items: Warehouse[] }>('/warehouses').then((r) => {
      setWarehouses(r.data.items);
      if (r.data.items[0]) setWarehouseId(r.data.items[0].id);
    });
  }, []);

  const start = async (): Promise<void> => {
    if (!warehouseId) return;
    setCreating(true);
    try {
      const r = await api().post<{ id: string }>('/stocktakes', { warehouseId });
      navigate(`/inventory/stocktakes/${r.data.id}`);
    } catch { toast.error(t('createFailed', { defaultValue: 'Could not start the count.' })); } finally { setCreating(false); }
  };

  if (!rows) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><ClipboardCheck className="h-6 w-6 text-brand" aria-hidden />{t('title', { defaultValue: 'Stocktake' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('subtitle', { defaultValue: 'Count physical stock per warehouse and post the variance.' })}</p>
      </div>
      {canWrite && warehouses.length > 0 && (
        <div className="flex items-end gap-2">
          <div className="w-56">
            <Combobox
              value={warehouseId}
              onChange={(v) => setWarehouseId(v ?? '')}
              options={warehouses.map((w) => ({ value: w.id, label: `${w.code} · ${w.name}` }))}
              placeholder={t('warehouse', { defaultValue: 'Warehouse' })}
            />
          </div>
          <Button onClick={start} loading={creating} disabled={!warehouseId}>
            <Plus className="h-4 w-4" aria-hidden />{t('newCount', { defaultValue: 'New count' })}
          </Button>
        </div>
      )}
    </div>
  );

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState
          icon={<ClipboardCheck className="h-8 w-8" aria-hidden />}
          title={t('empty', { defaultValue: 'No counts yet' })}
          description={t('emptyDescription', { defaultValue: 'Start a count for a warehouse to reconcile system stock with what is physically on the shelf.' })}
        />
      </div>
    );
  }

  const columns: DataTableColumn<StocktakeSummary>[] = [
    { id: 'docNumber', header: t('columns.number', { defaultValue: 'Number' }), sortValue: (r) => r.docNumber, searchValue: (r) => r.docNumber, cell: (r) => <span className="font-mono text-xs">{r.docNumber}</span> },
    { id: 'warehouse', header: t('warehouse', { defaultValue: 'Warehouse' }), sortValue: (r) => r.warehouseName, searchValue: (r) => r.warehouseName, cell: (r) => r.warehouseName },
    { id: 'status', header: t('columns.status', { defaultValue: 'Status' }), sortValue: (r) => r.status, cell: (r) => <Badge variant={STATUS_TONE[r.status] ?? 'default'}>{t(`status.${r.status}`, { defaultValue: r.status })}</Badge> },
    { id: 'progress', header: t('columns.counted', { defaultValue: 'Counted' }), align: 'end', sortValue: (r) => r.countedCount, cellClassName: 'font-mono tabular-nums', cell: (r) => `${r.countedCount}/${r.lineCount}` },
    { id: 'variance', header: t('columns.variance', { defaultValue: 'Variance value' }), align: 'end', sortValue: (r) => Number(r.varianceValue), cellClassName: 'font-mono tabular-nums', cell: (r) => Number(r.varianceValue) === 0 ? '—' : <span className={Number(r.varianceValue) < 0 ? 'text-danger' : 'text-success'}>{r.varianceValue}</span> },
    { id: 'createdAt', header: t('columns.created', { defaultValue: 'Created' }), sortValue: (r) => r.createdAt, cellClassName: 'text-fg-muted', cell: (r) => formatDateTime(r.createdAt, { locale: i18n.language }) },
  ];

  return (
    <div className="flex flex-col gap-4">
      {header}
      <Card className="overflow-hidden p-0">
        <DataTable columns={columns} data={rows} rowKey={(r) => r.id} onRowClick={(r) => navigate(`/inventory/stocktakes/${r.id}`)} />
      </Card>
    </div>
  );
}
